// Gen_EzTree — Set14 P3 mesh generator (§4.1/§4.2/§4.7): a pure function
// `(params, variantSeed) → SceneryMesh`, run in the existing TerrainWorker for the atlas bake.
// REPOINTS the scenery generation call-tree at the ez-tree fork (`@venryx/ez-tree`) in place of
// the home-grown `Gen_PineTree` (whose output quality is the reason this set exists).
//
// THREE TRANSFORMS applied after ez-tree's `createGeometry` (in this order, §4.2):
//   (a) Y-up → Z-up: ez-tree grows along +Y; our mesh contract is X/Y ground, Z up. Positions AND
//       normals remap `(x,y,z) → (x, −z, y)` — a RIGHT-HANDED remap (a naive Y/Z swap flips
//       handedness and inverts triangle winding → a back-faced mesh, §7).
//   (b) `targetHeight` (metres) → uniform scale: `targetHeight / measuredMaxZ`. Presets are
//       28–84 ez-tree units tall with no defined unit (§2.1); uniform scaling preserves the
//       preset's own crown proportions (§4.2 b — rejected separate targetWidth/aspectStretch).
//   (c) Grounding via nullable `sinkDepth` (§4.2 c): `null` = no adjustment (native min-Z, so a
//       `Bush 1` that dips below the origin keeps its intentional skirt); a NUMBER = auto-ground
//       first (translate so measured min-Z = 0), THEN sink by that amount. ⚠ NEVER compare a
//       possibly-absent `sinkDepth` with a bare `>`/`<` — branch on `== null` explicitly (Set13 §8).
//
// Emits the §4.7 `parts[]` contract: one `bark` part + one `leaf` part (bark = opaque FrontSide,
// leaf = alpha-tested DoubleSide, different bake treatment — a single-mesh contract can't express
// that). Indices are Uint32 (§2.3). Per-variant seed (§4.6) is threaded in via `variantSeed` —
// changing it alone yields a structurally different tree at zero param-swing cost.
import {Tree, type LODDetail} from "@venryx/ez-tree";
import * as THREE from "three";
import type {SceneryMesh, SceneryMeshPart, GenParams} from "../@SceneryTypes.js";
import {flatToTreeOptions} from "./EzTreeParams.js";

export const GEN_EZTREE_ID = "ezTree";

/** A variant's resolved param set is a flat `Record<string, ParamValue>` (GenParams); ez-tree's
 *  `TreeOptions` is nested. `flatToTreeOptions` owns the mapping (EzTreeParams.ts). The flat
 *  `sinkDepth`/`targetHeight` are NOT ez-tree options — they are scenery-level and read here. */
export interface EzTreeGenParams extends GenParams {}

/** Default targetHeight (metres) when the flat params don't specify one. Pine Medium measures
 *  51.1 ez-tree units → a 15 m tree. */
const DEFAULT_TARGET_HEIGHT = 15;

/** Read a numeric flat param, falling back when absent/NaN (never a bare `>`/`<` against a
 *  possibly-absent field — resolve through a default first, Set13 §8). */
function num(params: GenParams, key: string, fallback: number): number {
	const v = params[key];
	return (typeof v == "number" && Number.isFinite(v)) ? v : fallback;
}

/** A right-handed Y-up → Z-up remap applied to a position/normal triples array IN PLACE. Maps
 *  `(x, y, z) → (x, −z, y)` (new X = old X, new Y = −old Z, new Z = old Y). This is the transform
 *  that does NOT flip handedness (a naive `(x,z,y)` swap would). Exported for the §7 winding guard. */
export function remapYUpToZUp(positionsOrNormals: Float32Array): Float32Array {
	const out = new Float32Array(positionsOrNormals.length);
	for (let i = 0; i < positionsOrNormals.length; i += 3) {
		const x = positionsOrNormals[i], y = positionsOrNormals[i + 1], z = positionsOrNormals[i + 2];
		out[i] = x;
		out[i + 1] = -z;
		out[i + 2] = y;
	}
	return out;
}

/** Generate the ez-tree mesh for a resolved variant's flat params + a per-variant seed (§4.6).
 *  Deterministic from `(params, variantSeed)`. Bounded triangle count (max measured ~20 000 across
 *  presets; the P6 test caps at 50 000). Z-up, metres. Ground plane per the nullable `sinkDepth`. */
export function generateEzTree(params: GenParams, variantSeed = 0): SceneryMesh {
	// Resolve scenery-level params (NOT ez-tree options — read here, applied as transforms).
	const targetHeight = num(params, "targetHeight", DEFAULT_TARGET_HEIGHT);
	const sinkDepth = params.sinkDepth == null ? null : num(params, "sinkDepth", 0);

	// Build the ez-tree TreeOptions from the flat params + seed. A FRESH TreeOptions each call
	// (never shared/mutated across variants).
	const tree = new Tree(flatToTreeOptions(params));
	tree.options.seed = variantSeed >>> 0;

	// The detail/LOD knobs (§4.4): sectionStride/segmentFactor (branch density), leafStride/leafScale
	// (canopy density). Exposed as flat params (int/scalar) in the Detail/LOD group.
	const detail: LODDetail = {
		sectionStride: Math.max(1, Math.floor(num(params, "sectionStride", 1))),
		segmentFactor: Math.max(0.1, num(params, "segmentFactor", 1)),
		leafStride: Math.max(1, Math.floor(num(params, "leafStride", 1))),
		leafScale: Math.max(0.1, num(params, "leafScale", 1)),
	};
	const leafBillboard = params.leafBillboard;
	if (typeof leafBillboard == "string") {
		detail.billboard = leafBillboard == "single" ? "single" : "double";
	}

	const geo = tree.createGeometry(detail);
	const branches = geo.branches;
	const leaves = geo.leaves;

	// ── Transform (a): Y-up → Z-up (positions + normals), for BOTH parts. ──
	// The part's own arrays are the raw Y-up ez-tree arrays; remap in place (a copy is fine here —
	// we're about to scale anyway).
	const branchPos = remapYUpToZUp(branches.attributes.position.array as Float32Array);
	const branchNorm = branches.attributes.normal ? remapYUpToZUp(branches.attributes.normal.array as Float32Array) : new Float32Array();
	const leafPos = remapYUpToZUp(leaves.attributes.position.array as Float32Array);
	const leafNorm = leaves.attributes.normal ? remapYUpToZUp(leaves.attributes.normal.array as Float32Array) : new Float32Array();

	// ── Transform (b): uniform scale from `targetHeight` / measuredMaxZ (over the WHOLE mesh so
	// both parts scale together). ──
	const allPos = [branchPos, leafPos];
	let measuredMaxZ = -Infinity;
	let minZ = Infinity;
	let halfExtentXY = 0;
	for (const pos of allPos) {
		for (let i = 0; i < pos.length; i += 3) {
			const z = pos[i + 2];
			if (z > measuredMaxZ) measuredMaxZ = z;
			if (z < minZ) minZ = z;
			const x = pos[i], y = pos[i + 1];
			const r = Math.hypot(x, y);
			if (r > halfExtentXY) halfExtentXY = r;
		}
	}
	const scale = (Number.isFinite(measuredMaxZ) && measuredMaxZ > 1e-9) ? targetHeight / measuredMaxZ : 1;
	const scaleAndGround = (pos: Float32Array, norm: Float32Array): {pos: Float32Array; norm: Float32Array} => {
		for (let i = 0; i < pos.length; i += 3) {
			pos[i] *= scale;
			pos[i + 1] *= scale;
			pos[i + 2] *= scale;
		}
		// Grounding (c): after scaling, translate so the FINAL min-Z lands where the sinkDepth
		// dictates. `null` = no adjustment (native min-Z kept). A number = auto-ground first, then
		// sink (so measured min-Z == −sinkDepth).
		let dz = 0;
		if (sinkDepth != null) dz = -minZ * scale - sinkDepth; // bring min-Z to 0, then down by sinkDepth
		if (dz != 0) for (let i = 0; i < pos.length; i += 3) pos[i + 2] += dz;
		return {pos, norm};
	};
	const {pos: branchPosG, norm: branchNormG} = scaleAndGround(branchPos, branchNorm);
	const {pos: leafPosG, norm: leafNormG} = scaleAndGround(leafPos, leafNorm);

	// ── Build the parts ──
	const branchIdx = branches.index as THREE.BufferAttribute;
	const leafIdx = leaves.index as THREE.BufferAttribute;
	const parts: SceneryMeshPart[] = [
		{
			positions: branchPosG,
			normals: branchNormG,
			uvs: new Float32Array(branches.attributes.uv ? branches.attributes.uv.array : []),
			indices: new Uint32Array(branchIdx.array),
			materialKind: "bark",
		},
		{
			positions: leafPosG,
			normals: leafNormG,
			uvs: new Float32Array(leaves.attributes.uv ? leaves.attributes.uv.array : []),
			indices: new Uint32Array(leafIdx.array),
			materialKind: "leaf",
		},
	];

	// Post-scale measured maxZ / halfExtent (the values the contract promises).
	let finalMaxZ = -Infinity, finalMinZ = Infinity, finalHalfExtent = 0;
	for (const p of parts) {
		for (let i = 0; i < p.positions.length; i += 3) {
			const z = p.positions[i + 2];
			if (z > finalMaxZ) finalMaxZ = z;
			if (z < finalMinZ) finalMinZ = z;
			const r = Math.hypot(p.positions[i], p.positions[i + 1]);
			if (r > finalHalfExtent) finalHalfExtent = r;
		}
	}

	return {
		parts,
		maxZ: Number.isFinite(finalMaxZ) ? finalMaxZ : targetHeight,
		halfExtentXY: Number.isFinite(finalHalfExtent) ? finalHalfExtent : 1,
	};
}
